/**
 * Decide quais requisições HTTP viram span de tracing no Sentry.
 *
 * O SDK instrumenta todo fetch/XHR da página, inclusive o de scripts que não são
 * nossos. Em 08/2026 isso respondia por ~40% dos spans do projeto: telemetria de
 * terceiros (bugsnag, rudderstack), asset de CDN e o ping de reachability do
 * NetInfo — nenhum deles diz nada sobre o app e todos consomem cota.
 */

const UNTRACKED_REQUEST_DOMAINS = [
  'bugsnag.com',
  'rudderstack.com',
  'jsdelivr.net',
  // Gravação de sessão do Hotjar: ~47 mil spans/mês, nenhum deles sobre o app.
  'hotjar.io',
  'hotjar.com',
]

const ABSOLUTE_URL = /^[a-z][a-z0-9+.-]*:\/\/([^/?#]+)([^?#]*)/i

interface IUrlParts {
  host: string
  path: string
}

function splitUrl(url: string): IUrlParts {
  const absolute = ABSOLUTE_URL.exec(url)

  if (absolute) {
    const authority = absolute[1]
    const hostWithPort = authority.slice(authority.lastIndexOf('@') + 1)

    return {
      host: hostWithPort.replace(/:\d+$/, '').toLowerCase(),
      path: absolute[2] || '/',
    }
  }

  return { host: '', path: url.split(/[?#]/)[0] }
}

function isUntrackedHost(host: string): boolean {
  return UNTRACKED_REQUEST_DOMAINS.some(
    (domain) => host === domain || host.endsWith(`.${domain}`),
  )
}

export function shouldTraceRequest(url: string): boolean {
  if (typeof url !== 'string' || url.length === 0) return true

  const { host, path } = splitUrl(url)

  if (host && isUntrackedHost(host)) return false

  // Requisição na raiz é o ping de reachability do NetInfo, que roda de minuto em
  // minuto por sessão aberta. O app nunca busca dado na raiz de um host.
  if (path === '' || path === '/') return false

  return true
}
